import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _templateObject, _templateObject2;
import { css } from "@emotion/core";
import styled from "@emotion/styled";
import { createFocusRingStyles } from "../../../util/createFocusRingStyles";
import { withFocusToggleClassName } from "../../../util/withFocusClassName";
import { checkboxBorderTheme, checkboxColors, checkboxMotionTheme } from "../checkboxTheme";
var getSize = function getSize(size) {
  return size;
};
// These need to be set on the parent element for teams to be able to override easily
export var checkboxCssVars = function checkboxCssVars(size) {
  return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    --smar--sds--checkbox--visual-size: ", "px;\n"])), getSize(size));
};
export var CheckboxIcon = withFocusToggleClassName(styled("div")(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    // Both of these are needed to position the input fully over the icon.\n    position: relative;\n    isolation: isolate;\n    // These don't use the css var since they're used for the outer tap target size.\n    width: ", "px;\n    height: ", "px;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    // Focus ring.\n    ", "\n\n    // The following styles are used to drive the checkbox styling entirely via CSS, rather than\n    // using CSS in JS. This is for performance in the Grid and other complex UI. A single\n    // re-render, React dev-mode on a very fast computer, is usually around 1.3ms. Keeping these\n    // hover changes in CSS contributes to a larger ecosystem of perf.\n\n    //  Base Styles\n    > input + [data-checkbox-wrapper],\n    > input + [data-checkbox-wrapper] > * {\n        transition: ", ";\n    }\n\n    > input + [data-checkbox-wrapper] > * {\n        width: var(--smar--sds--checkbox--visual-size, ", "px);\n    }\n\n    // Rest unchecked\n    > input:not(:checked) + [data-checkbox-wrapper] {\n        background-color: ", ";\n        border-color: ", ";\n        > * {\n            color: ", ";\n        }\n        // to handle the dual-tone unchecked flag/star outline\n        path + path {\n            color: ", ";\n        }\n    }\n\n    > input:not(:checked):not(:indeterminate) + div span svg path {\n        stroke-dasharray: 1000;\n        stroke-dashoffset: 1000;\n    }\n\n    // Rest checked\n    > input:checked + [data-checkbox-wrapper],\n    > input:indeterminate + [data-checkbox-wrapper] {\n        background-color: ", ";\n        border-color: ", ";\n    }\n\n    > input:checked + [data-checkbox-wrapper] > * {\n        color: ", ";\n    }\n\n    > input:checked + div span svg path {\n        // these are only for the checkmark, but don't seem to negatively affect the others\n        stroke-dasharray: 1000;\n        stroke-dashoffset: 0;\n        transition: ", ";\n    }\n\n    // Hover unchecked\n    > input:not(:checked):hover + [data-checkbox-wrapper] {\n        background-color: ", ";\n        border-color: ", ";\n        > * {\n            color: ", ";\n        }\n    }\n\n    // Hover checked\n    > input:checked:hover + [data-checkbox-wrapper],\n    > input:indeterminate:hover + [data-checkbox-wrapper] {\n        background-color: ", ";\n        border-color: ", ";\n    }\n\n    // The check and dash icons have a different color when checked and hovered.\n    > input:not(:checked):hover + [data-checkbox-wrapper] > * {\n        color: ", ";\n    }\n    > input:checked:hover + [data-checkbox-wrapper] > *,\n    > input:indeterminate:hover + [data-checkbox-wrapper] > * {\n        color: ", ";\n    }\n\n    // Active unchecked\n    > input:not(:checked):active + [data-checkbox-wrapper] {\n        background-color: ", ";\n        border-color: ", ";\n        > * {\n            color: ", ";\n        }\n    }\n\n    // Active checked\n    > input:checked:active + [data-checkbox-wrapper],\n    > input:indeterminate:active + [data-checkbox-wrapper] {\n        background-color: ", ";\n        border-color: ", ";\n        > * {\n            color: ", ";\n        }\n    }\n\n    // Disabled unchecked - we currently support both the disabled attribute and the aria-disabled attribute,\n    // unfortunately, though we will remove the disabled attr support in a future major release.\n    > input:not(:checked):disabled + [data-checkbox-wrapper],\n    > input:not(:checked)[aria-disabled=\"true\"] + [data-checkbox-wrapper] {\n        background-color: ", ";\n        border-color: ", ";\n        > * {\n            color: ", ";\n        }\n        path + path {\n            color: ", ";\n        }\n    }\n\n    // Disabled checked\n    > input:checked:disabled + [data-checkbox-wrapper],\n    > input:checked[aria-disabled=\"true\"] + [data-checkbox-wrapper] {\n        background-color: ", ";\n        border-color: ", ";\n        > * {\n            color: ", ";\n        }\n    }\n\n    // Invalid unchecked rest\n    > input:not(:checked)[data-is-invalid=\"true\"] + [data-checkbox-wrapper] {\n        // Invalid unchecked rest uses the same background styles as valid unchecked rest.\n        background-color: ", ";\n        border-color: ", ";\n        path + path {\n            color: ", ";\n        }\n    }\n\n    // Invalid checked rest\n    > input:checked[data-is-invalid=\"true\"] + [data-checkbox-wrapper] {\n        background-color: ", ";\n        border-color: ", ";\n        > * {\n            color: ", ";\n        }\n    }\n\n    // Invalid unchecked hover\n    > input:not(:checked)[data-is-invalid=\"true\"]:hover + [data-checkbox-wrapper] {\n        // Invalid unchecked hover uses the same background styles as valid unchecked hover.\n        background-color: ", ";\n        border-color: ", ";\n        path + path {\n            color: ", ";\n        }\n    }\n\n    // Invalid checked hover\n    > input:checked[data-is-invalid=\"true\"]:hover + [data-checkbox-wrapper] {\n        background-color: ", ";\n        border-color: ", ";\n        > * {\n            color: ", ";\n        }\n    }\n\n    // Invalid unchecked active\n    > input:not(:checked)[data-is-invalid=\"true\"]:active + [data-checkbox-wrapper] {\n        // Invalid unchecked active uses the same background styles as valid unchecked active.\n        background-color: ", ";\n        border-color: ", ";\n        path + path {\n            color: ", ";\n        }\n    }\n\n    // Invalid checked active\n    > input:checked[data-is-invalid=\"true\"]:active + [data-checkbox-wrapper] {\n        background-color: ", ";\n        border-color: ", ";\n        > * {\n            color: ", ";\n        }\n    }\n"])), function (_ref) {
  var size = _ref.size;
  return getSize(size);
}, function (_ref2) {
  var size = _ref2.size;
  return getSize(size);
}, function (_ref3) {
  var themeMode = _ref3.themeMode;
  return createFocusRingStyles({
    theme: themeMode,
    gap: 1,
    // 0 here since the element this is attached to doesn't have a border.
    componentBorderWidth: 0,
    componentRadius: checkboxBorderTheme.borderRadius(themeMode),
    focusSelector: ":focus-within"
  });
}, function (_ref4) {
  var themeMode = _ref4.themeMode;
  return checkboxMotionTheme.background(themeMode);
}, function (_ref5) {
  var size = _ref5.size;
  return getSize(size);
}, function (_ref6) {
  var themeMode = _ref6.themeMode,
    appearance = _ref6.appearance;
  return checkboxColors[appearance].background.rest.unchecked(themeMode);
}, function (_ref7) {
  var themeMode = _ref7.themeMode,
    appearance = _ref7.appearance;
  return checkboxColors[appearance].border.rest.unchecked(themeMode);
}, function (_ref8) {
  var themeMode = _ref8.themeMode,
    appearance = _ref8.appearance;
  return checkboxColors[appearance].icon.rest.unchecked(themeMode);
}, function (_ref9) {
  var themeMode = _ref9.themeMode,
    appearance = _ref9.appearance;
  return checkboxColors[appearance].icon.rest.outline(themeMode);
}, function (_ref10) {
  var themeMode = _ref10.themeMode,
    appearance = _ref10.appearance;
  return checkboxColors[appearance].background.rest.checked(themeMode);
}, function (_ref11) {
  var themeMode = _ref11.themeMode,
    appearance = _ref11.appearance;
  return checkboxColors[appearance].border.rest.checked(themeMode);
}, function (_ref12) {
  var themeMode = _ref12.themeMode,
    appearance = _ref12.appearance;
  return checkboxColors[appearance].icon.rest.checked(themeMode);
}, function (_ref13) {
  var themeMode = _ref13.themeMode,
    appearance = _ref13.appearance;
  return checkboxMotionTheme[appearance](themeMode);
}, function (_ref14) {
  var themeMode = _ref14.themeMode,
    appearance = _ref14.appearance;
  return checkboxColors[appearance].background.hover.unchecked(themeMode);
}, function (_ref15) {
  var themeMode = _ref15.themeMode,
    appearance = _ref15.appearance;
  return checkboxColors[appearance].border.hover.unchecked(themeMode);
}, function (_ref16) {
  var themeMode = _ref16.themeMode,
    appearance = _ref16.appearance;
  return checkboxColors[appearance].icon.hover.unchecked(themeMode);
}, function (_ref17) {
  var themeMode = _ref17.themeMode,
    appearance = _ref17.appearance;
  return checkboxColors[appearance].background.hover.checked(themeMode);
}, function (_ref18) {
  var themeMode = _ref18.themeMode,
    appearance = _ref18.appearance;
  return checkboxColors[appearance].border.hover.checked(themeMode);
}, function (_ref19) {
  var themeMode = _ref19.themeMode,
    appearance = _ref19.appearance;
  return checkboxColors[appearance].icon.hover.unchecked(themeMode);
}, function (_ref20) {
  var themeMode = _ref20.themeMode,
    appearance = _ref20.appearance;
  return checkboxColors[appearance].icon.hover.checked(themeMode);
}, function (_ref21) {
  var themeMode = _ref21.themeMode,
    appearance = _ref21.appearance;
  return checkboxColors[appearance].background.active.unchecked(themeMode);
}, function (_ref22) {
  var themeMode = _ref22.themeMode,
    appearance = _ref22.appearance;
  return checkboxColors[appearance].border.active.unchecked(themeMode);
}, function (_ref23) {
  var themeMode = _ref23.themeMode,
    appearance = _ref23.appearance;
  return checkboxColors[appearance].icon.active.unchecked(themeMode);
}, function (_ref24) {
  var themeMode = _ref24.themeMode,
    appearance = _ref24.appearance;
  return checkboxColors[appearance].background.active.checked(themeMode);
}, function (_ref25) {
  var themeMode = _ref25.themeMode,
    appearance = _ref25.appearance;
  return checkboxColors[appearance].border.active.checked(themeMode);
}, function (_ref26) {
  var themeMode = _ref26.themeMode,
    appearance = _ref26.appearance;
  return checkboxColors[appearance].icon.active.checked(themeMode);
}, function (_ref27) {
  var themeMode = _ref27.themeMode,
    appearance = _ref27.appearance;
  return checkboxColors[appearance].background.disabled.unchecked(themeMode);
}, function (_ref28) {
  var themeMode = _ref28.themeMode,
    appearance = _ref28.appearance;
  return checkboxColors[appearance].border.disabled.unchecked(themeMode);
}, function (_ref29) {
  var themeMode = _ref29.themeMode,
    appearance = _ref29.appearance;
  return checkboxColors[appearance].icon.disabled.unchecked(themeMode);
}, function (_ref30) {
  var themeMode = _ref30.themeMode,
    appearance = _ref30.appearance;
  return checkboxColors[appearance].icon.disabled.outline(themeMode);
}, function (_ref31) {
  var themeMode = _ref31.themeMode,
    appearance = _ref31.appearance;
  return checkboxColors[appearance].background.disabled.checked(themeMode);
}, function (_ref32) {
  var themeMode = _ref32.themeMode,
    appearance = _ref32.appearance;
  return checkboxColors[appearance].border.disabled.checked(themeMode);
}, function (_ref33) {
  var themeMode = _ref33.themeMode,
    appearance = _ref33.appearance;
  return checkboxColors[appearance].icon.disabled.checked(themeMode);
}, function (_ref34) {
  var themeMode = _ref34.themeMode,
    appearance = _ref34.appearance;
  return checkboxColors[appearance].background.rest.unchecked(themeMode);
}, function (_ref35) {
  var themeMode = _ref35.themeMode,
    appearance = _ref35.appearance;
  return checkboxColors[appearance].border.invalid.rest.unchecked(themeMode);
}, function (_ref36) {
  var themeMode = _ref36.themeMode,
    appearance = _ref36.appearance;
  return checkboxColors[appearance].icon.invalid.rest(themeMode);
}, function (_ref37) {
  var themeMode = _ref37.themeMode,
    appearance = _ref37.appearance;
  return checkboxColors[appearance].background.invalid.rest.checked(themeMode);
}, function (_ref38) {
  var themeMode = _ref38.themeMode,
    appearance = _ref38.appearance;
  return checkboxColors[appearance].border.invalid.rest.checked(themeMode);
}, function (_ref39) {
  var themeMode = _ref39.themeMode,
    appearance = _ref39.appearance;
  return checkboxColors[appearance].icon.invalid.rest(themeMode);
}, function (_ref40) {
  var themeMode = _ref40.themeMode,
    appearance = _ref40.appearance;
  return checkboxColors[appearance].background.hover.unchecked(themeMode);
}, function (_ref41) {
  var themeMode = _ref41.themeMode,
    appearance = _ref41.appearance;
  return checkboxColors[appearance].border.invalid.hover.unchecked(themeMode);
}, function (_ref42) {
  var themeMode = _ref42.themeMode,
    appearance = _ref42.appearance;
  return checkboxColors[appearance].icon.invalid.hover(themeMode);
}, function (_ref43) {
  var themeMode = _ref43.themeMode,
    appearance = _ref43.appearance;
  return checkboxColors[appearance].background.invalid.hover.checked(themeMode);
}, function (_ref44) {
  var themeMode = _ref44.themeMode,
    appearance = _ref44.appearance;
  return checkboxColors[appearance].border.invalid.hover.checked(themeMode);
}, function (_ref45) {
  var themeMode = _ref45.themeMode,
    appearance = _ref45.appearance;
  return checkboxColors[appearance].icon.invalid.hover(themeMode);
}, function (_ref46) {
  var themeMode = _ref46.themeMode,
    appearance = _ref46.appearance;
  return checkboxColors[appearance].background.active.unchecked(themeMode);
}, function (_ref47) {
  var themeMode = _ref47.themeMode,
    appearance = _ref47.appearance;
  return checkboxColors[appearance].border.invalid.active.unchecked(themeMode);
}, function (_ref48) {
  var themeMode = _ref48.themeMode,
    appearance = _ref48.appearance;
  return checkboxColors[appearance].icon.invalid.active(themeMode);
}, function (_ref49) {
  var themeMode = _ref49.themeMode,
    appearance = _ref49.appearance;
  return checkboxColors[appearance].background.invalid.active.checked(themeMode);
}, function (_ref50) {
  var themeMode = _ref50.themeMode,
    appearance = _ref50.appearance;
  return checkboxColors[appearance].border.invalid.active.checked(themeMode);
}, function (_ref51) {
  var themeMode = _ref51.themeMode,
    appearance = _ref51.appearance;
  return checkboxColors[appearance].icon.invalid.active(themeMode);
}));
//# sourceMappingURL=CheckboxIcon.js.map